import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "../config";
import KathakalaPlayer, { formatSeconds } from "../components/KathakalaPlayer";
import FollowCTA from "../components/episode/FollowCTA";
import StoryGraph from "../components/episode/StoryGraph";
import { honorific, isFolkWriter } from "../utils/namePrefix";
import {
  FaArrowLeft, FaChevronLeft, FaChevronRight, FaPlay, FaComments,
  FaYoutube, FaInstagram, FaFacebookF, FaUser, FaFilm, FaPalette, FaHeadphones, FaProjectDiagram, FaColumns,
} from "react-icons/fa";

// One-page story experience (2026-10-19, Bhaskar): a visitor who lands on a
// story gets everything here — play the story, play the analysis with its
// transcript as a live dialogue, the storyteller's profile, every reel of the
// story and of the analysis, and follow / subscribe / watch-LIVE prompts.
//
// 2026-10-20: everything plays inline — the picture-story video (oil-painting
// retelling, performances.picture_story_youtube_id) is the hero when present,
// then the podcast episode (performances.podcast_audio_url), then the reels
// as playable Shorts. Starting any of them pauses the others.
//
// Also శ్రీమతి వసుంధర విశ్లేషణ (Bhaskar's mother's review of each story:
// video, podcast, transcript) under the story's own analysis. Those columns
// (db/vasundhara-review-links.sql) are fetched separately so the page still
// works where they don't exist yet.
//
// Route: /episodes/:episodeId/stories/:performanceId  (?play=picture|story|
// analysis picks the opening player). Data comes from the after-show
// pipeline: performances.discussion_dialog, podcast_audio_url,
// picture_story_youtube_id and story_reels.{performance_id, kind,
// youtube_url, instagram_url, facebook_url, published_at}
// (db/postshow-reels-and-dialog.sql).
//
// Public page: never select storytellers.whatsapp / email, and no ratings or
// scores (product requirements: public pages show no scores).

const PERF_COLS =
  "performance_id, episode_id, story_id, storyteller_id, storyteller_name_telugu, storyteller_name_english, storytellers(gender), " +
  "story_start_sec, story_end_sec, discussion_start_sec, discussion_end_sec, " +
  "podcast_audio_url, picture_story_youtube_id";

const SPEAKER_COLORS = [
  "text-kk-gold", "text-sky-300", "text-emerald-300", "text-rose-300", "text-violet-300", "text-amber-200",
];

// Spoken fillers dropped from the dialogue (Bhaskar, 2026-10-19): only
// these exact standalone words, so longer words that contain them stay.
const FILLERS = new Set(["ఉమ్", "ఉం", "ఉమ్మ్", "హ్మ్", "హమ్", "సో"]);

const cleanLine = (text) =>
  (text || "")
    .split(/\s+/)
    .filter((tok) => !FILLERS.has(tok.replace(/[.,!?…:;]+$/, "")))
    .join(" ")
    .trim();

// Related-story lists (storyteller / writer / genre / same story), newest
// show first. Some episodes hold the same story twice (two stories rows /
// performances, one without a start time) and some performances have no
// story linked: show each story once per episode, prefer the playable copy,
// skip the unlinked ones and this very story.
const REL_COLS =
  "performance_id, episode_id, story_id, story_start_sec, storyteller_name_telugu, storytellers(gender), " +
  "episodes(publish_date, scheduled_start), stories(title_telugu, genre)";

// A performance's storyteller with శ్రీ / శ్రీమతి.
const tellerOf = (o) => honorific(o.storyteller_name_telugu, o.storytellers?.gender);

const showDate = (o) => o.episodes?.scheduled_start || o.episodes?.publish_date || "";

const relatedList = (rows, perf, currentTitle) => {
  const seen = new Set();
  return (rows || [])
    .filter((o) => o.story_id && o.stories?.title_telugu)
    .sort(
      (a, b) =>
        showDate(b).localeCompare(showDate(a)) ||
        (b.story_start_sec != null) - (a.story_start_sec != null)
    )
    .filter((o) => {
      const t = o.stories.title_telugu.trim();
      if (o.performance_id === perf.performance_id) return false;
      if (o.episode_id === perf.episode_id && (o.story_id === perf.story_id || t === currentTitle)) return false;
      const key = `${o.episode_id}:${t}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

// "#హాస్యం, #వ్యంగ్యం, #కుటుంబ_కథ" → ["హాస్యం", "వ్యంగ్యం", "కుటుంబ_కథ"]
const genreTags = (genre) =>
  [...new Set((genre || "").split(/[,\s]+/).map((t) => t.replace(/^#+/, "").trim()).filter(Boolean))];

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        timeZone: "America/Chicago", month: "short", day: "numeric", year: "numeric",
      })
    : "";

const hasRange = (a, b) => a != null && b != null && b > a;

// YouTube video id from a Shorts / watch / youtu.be link.
const youtubeId = (url) =>
  (url || "").match(/(?:shorts\/|[?&]v=|youtu\.be\/|embed\/)([\w-]{11})/)?.[1] || null;

const VASUNDHARA_PLAYLIST = "https://www.youtube.com/playlist?list=PLbnFQAAV_G6s";
const VASUNDHARA_FEED = "https://podcast.kathakala.us/vasundhara/feed.xml";

// Pause a youtube-nocookie iframe embedded with enablejsapi=1.
const ytCommand = (iframe, func) =>
  iframe?.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args: [] }), "*");

export default function StoryPage() {
  const { episodeId, performanceId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // ?view=graph shows the కథాజాలం · Story Graph instead of the page (links between story
  // maps keep it, so a visitor can travel the archive as a graph).
  const view = searchParams.get("view") === "graph" ? "graph" : "page";
  const setView = (v) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === "graph") next.set("view", "graph");
        else next.delete("view");
        return next;
      },
      { replace: true }
    );

  const [state, setState] = useState({ loading: true });
  // null = automatic: the picture story when there is one, else the LIVE story.
  const [mode, setMode] = useState(
    ["picture", "story", "analysis"].includes(searchParams.get("play")) ? searchParams.get("play") : null
  );
  const [tab, setTab] = useState(null);
  const [now, setNow] = useState(0);
  const playerRef = useRef(null);
  const pictureRef = useRef(null);
  const [podcastLength, setPodcastLength] = useState(null);
  const dialogBoxRef = useRef(null);
  const lineRefs = useRef([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState({ loading: true });
      const { data: episode } = await supabase
        .from("episodes")
        .select("episode_id, title, video_id, youtube_broadcast_id, publish_date, scheduled_start")
        .eq("episode_id", episodeId)
        .maybeSingle();
      const { data: siblings } = await supabase
        .from("performances")
        .select(PERF_COLS)
        .eq("episode_id", episodeId)
        .order("story_start_sec", { ascending: true, nullsFirst: false });
      const perf = (siblings || []).find((p) => String(p.performance_id) === String(performanceId));
      if (!episode || !perf) {
        if (!cancelled) setState({ loading: false, notFound: true });
        return;
      }

      const storyIds = [...new Set((siblings || []).map((p) => p.story_id).filter(Boolean))];
      const [dialogRes, storiesRes, tellerRes, reelsRes, otherRes, vasRes] = await Promise.all([
        supabase.from("performances").select("discussion_dialog").eq("performance_id", perf.performance_id).maybeSingle(),
        storyIds.length
          ? supabase.from("stories").select("story_id, title_telugu, writer_id, genre, writers(name_telugu, name_english, gender)").in("story_id", storyIds)
          : Promise.resolve({ data: [] }),
        perf.storyteller_id
          ? supabase
              .from("storytellers")
              .select("storyteller_id, storyteller_telugu, storyteller_english, gender, photo_url, introduction")
              .eq("storyteller_id", perf.storyteller_id)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        supabase
          .from("story_reels")
          .select("reel_id, kind, youtube_url, instagram_url, facebook_url, published_at")
          .eq("performance_id", perf.performance_id)
          .order("published_at", { ascending: true }),
        perf.storyteller_id
          ? supabase
              .from("performances")
              .select(REL_COLS)
              .eq("storyteller_id", perf.storyteller_id)
              .neq("performance_id", perf.performance_id)
              .order("episode_id", { ascending: false })
              .limit(100)
          : Promise.resolve({ data: [] }),
        supabase
          .from("performances")
          .select("vasundhara_review_youtube_id, vasundhara_podcast_audio_url, vasundhara_transcript")
          .eq("performance_id", perf.performance_id)
          .maybeSingle(),
      ]);
      if (cancelled) return;

      const storyMap = {};
      (storiesRes.data || []).forEach((s) => (storyMap[s.story_id] = s));
      const reels = (reelsRes.data || []).filter((r) => r.youtube_url || r.instagram_url || r.facebook_url);
      const dialog = (Array.isArray(dialogRes.data?.discussion_dialog) ? dialogRes.data.discussion_dialog : [])
        .map((d) => ({ ...d, text: cleanLine(d.text) }))
        .filter((d) => /[^\s.,!?…:;]/.test(d.text));

      setState({
        loading: false,
        episode,
        perf,
        siblings: siblings || [],
        storyMap,
        teller: tellerRes.data,
        reels,
        dialog,
        vasundhara: vasRes.error ? null : vasRes.data,
        others: relatedList(
          otherRes.error ? [] : otherRes.data,
          perf,
          (storyMap[perf.story_id]?.title_telugu || "").trim()
        ),
      });
      setTab(dialog.length ? "dialog" : "storyteller");
    })();
    return () => {
      cancelled = true;
    };
  }, [episodeId, performanceId]);

  const { episode, perf, siblings, storyMap, teller, reels, dialog, others, vasundhara } = state;
  const story = perf ? storyMap[perf.story_id] : null;
  const videoId = episode?.youtube_broadcast_id || episode?.video_id;
  const canStory = perf && hasRange(perf.story_start_sec, perf.story_end_sec);
  const canAnalysis = perf && hasRange(perf.discussion_start_sec, perf.discussion_end_sec);
  const pictureId = perf?.picture_story_youtube_id || null;
  const effectiveMode =
    mode === "analysis" && canAnalysis ? "analysis"
    : mode === "story" && canStory ? "story"
    : (mode === null || mode === "picture") && pictureId ? "picture"
    : canStory ? "story"
    : canAnalysis ? "analysis"
    : pictureId ? "picture"
    : "story";
  const segment =
    effectiveMode === "analysis"
      ? [perf?.discussion_start_sec, perf?.discussion_end_sec]
      : [perf?.story_start_sec, perf?.story_end_sec];

  // Writer / genre / same-story lists load after the page has rendered.
  const [related, setRelated] = useState(null);
  useEffect(() => {
    if (!perf || !story) return;
    let cancelled = false;
    setRelated(null);
    const currentTitle = (story.title_telugu || "").trim();
    const perfsOf = async (ids) => {
      if (!ids.length) return [];
      const { data, error } = await supabase
        .from("performances")
        .select(REL_COLS)
        .in("story_id", ids.slice(0, 150))
        .limit(300);
      return error ? [] : data || [];
    };
    (async () => {
      const tags = genreTags(story.genre);
      const [byWriter, byTitle, byGenre] = await Promise.all([
        story.writer_id
          ? supabase.from("stories").select("story_id, title_telugu").eq("writer_id", story.writer_id).limit(300)
          : Promise.resolve({ data: [] }),
        currentTitle
          ? supabase.from("stories").select("story_id, writer_id").eq("title_telugu", currentTitle).limit(50)
          : Promise.resolve({ data: [] }),
        tags.length
          ? supabase
              .from("stories")
              .select("story_id, title_telugu, genre")
              .or(tags.map((t) => `genre.ilike."*${t}*"`).join(","))
              .limit(400)
          : Promise.resolve({ data: [] }),
      ]);

      // Same story: this story_id, or the same title by the same writer
      // (duplicate stories rows), told in other episodes / by others.
      const sameIds = [
        perf.story_id,
        ...(byTitle.data || [])
          .filter((r) => !story.writer_id || !r.writer_id || r.writer_id === story.writer_id)
          .map((r) => r.story_id),
      ];
      // Writer: the writer's other stories (not this one).
      const writerIds = (byWriter.data || [])
        .filter((r) => (r.title_telugu || "").trim() !== currentTitle)
        .map((r) => r.story_id);
      // Genre: rank by how many tags are shared.
      const score = {};
      (byGenre.data || []).forEach((r) => {
        if ((r.title_telugu || "").trim() === currentTitle) return;
        const shared = genreTags(r.genre).filter((t) => tags.includes(t)).length;
        if (shared) score[r.story_id] = shared;
      });
      const genreIds = Object.keys(score)
        .map(Number)
        .sort((a, b) => score[b] - score[a])
        .slice(0, 80);

      const [same, writer, genre] = await Promise.all([perfsOf(sameIds), perfsOf(writerIds), perfsOf(genreIds)]);
      if (cancelled) return;
      setRelated({
        same: relatedList(same, perf, currentTitle).filter((o) => o.episode_id !== perf.episode_id),
        writer: relatedList(writer, perf, currentTitle),
        genre: relatedList(genre, perf, currentTitle)
          .map((o) => ({ ...o, shared: genreTags(o.stories.genre).filter((t) => tags.includes(t)) }))
          .sort((a, b) => b.shared.length - a.shared.length || showDate(b).localeCompare(showDate(a)))
          .slice(0, 40),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [perf, story]);

  // Names carry శ్రీ / శ్రీమతి everywhere they are shown (never doubled).
  const tellerGender = teller?.gender || perf?.storytellers?.gender;
  const tellerTe = honorific(teller?.storyteller_telugu || perf?.storyteller_name_telugu, tellerGender);
  const tellerEn = honorific(teller?.storyteller_english || perf?.storyteller_name_english, tellerGender, "english");
  const writer = story?.writers;
  const folk = isFolkWriter(writer?.name_telugu);
  const writerTe = folk ? "జానపద కథ" : honorific(writer?.name_telugu, writer?.gender);
  const writerEn = folk ? "" : honorific(writer?.name_english, writer?.gender, "english");
  const title = story?.title_telugu || "కథ";

  useEffect(() => {
    if (!perf) return;
    const before = document.title;
    document.title = `${title} — ${tellerTe || tellerEn || ""} | Kathakala`;
    return () => {
      document.title = before;
    };
  }, [perf, title, tellerTe, tellerEn]);

  // The player is created once per story with the opening segment; switching
  // between story and analysis (or jumping to a dialogue line) loads a new
  // segment into the same player.
  const initialSegment = useMemo(() => segment, [perf?.performance_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only one thing plays at a time. `keep` is "live" (the LIVE player), the
  // <audio> / <iframe> element that just started, or nothing (pause all).
  const pauseAll = useCallback((keep) => {
    if (keep !== "live") playerRef.current?.pauseVideo();
    document.querySelectorAll("audio").forEach((a) => a !== keep && a.pause());
    document.querySelectorAll("iframe[data-pausable]").forEach((f) => f !== keep && ytCommand(f, "pauseVideo"));
  }, []);

  const showPicture = useCallback(() => {
    pauseAll(pictureRef.current);
    setMode("picture");
  }, [pauseAll]);

  const play = useCallback(
    (m, fromSec) => {
      if (!perf) return;
      pauseAll("live");
      setMode(m);
      const [s, e] = m === "analysis"
        ? [perf.discussion_start_sec, perf.discussion_end_sec]
        : [perf.story_start_sec, perf.story_end_sec];
      playerRef.current?.playSegment(fromSec ?? s, e);
    },
    [perf, pauseAll]
  );

  // Speaker → colour, in order of first appearance.
  const speakerColor = useMemo(() => {
    const map = {};
    dialog?.forEach((d) => {
      const k = d.speaker_en || d.speaker_te || "?";
      if (!(k in map)) map[k] = SPEAKER_COLORS[Object.keys(map).length % SPEAKER_COLORS.length];
    });
    return map;
  }, [dialog]);

  const activeLine = useMemo(() => {
    if (!dialog?.length || effectiveMode !== "analysis") return -1;
    let idx = -1;
    for (let i = 0; i < dialog.length; i++) {
      if (dialog[i].start_sec <= now + 0.5) idx = i;
      else break;
    }
    if (idx >= 0 && dialog[idx].end_sec != null && now > dialog[idx].end_sec + 2 && idx === dialog.length - 1) return -1;
    return idx;
  }, [dialog, now, effectiveMode]);

  // Keep the current line in view inside the dialogue box (without scrolling
  // the page itself).
  useEffect(() => {
    const box = dialogBoxRef.current;
    const el = lineRefs.current[activeLine];
    if (!box || !el || tab !== "dialog") return;
    const top = el.offsetTop - box.offsetTop;
    if (top < box.scrollTop || top + el.offsetHeight > box.scrollTop + box.clientHeight) {
      box.scrollTo({ top: Math.max(0, top - box.clientHeight / 3), behavior: "smooth" });
    }
  }, [activeLine, tab]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-kk-ink flex items-center justify-center">
        <div className="w-14 h-14 border-4 border-kk-gold/30 border-t-kk-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (state.notFound) {
    return (
      <div className="min-h-screen bg-kk-ink flex flex-col items-center justify-center text-center px-6">
        <h1 className="text-2xl font-bold text-kk-cream mb-4">Story not found</h1>
        <button
          onClick={() => navigate(`/episodes/${episodeId}`)}
          className="px-6 py-2 bg-kk-gold text-kk-ink rounded-full font-semibold hover:bg-kk-amber transition-colors"
        >
          Back to the episode
        </button>
      </div>
    );
  }

  const idx = siblings.findIndex((p) => p.performance_id === perf.performance_id);
  const prev = idx > 0 ? siblings[idx - 1] : null;
  const next = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;
  const tabs = [
    dialog.length ? { id: "dialog", label: "విశ్లేషణ సంభాషణ", sub: "Analysis dialogue", Icon: FaComments } : null,
    { id: "storyteller", label: "కథకులు", sub: "Storyteller", Icon: FaUser },
  ].filter(Boolean);
  const activeTab = tabs.some((t) => t.id === tab) ? tab : tabs[0].id;

  // ── story graph nodes ──
  const thumb = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
  const graphMedia = [
    pictureId && { id: "picture", label: "చిత్ర కథ", sub: "Picture story", image: thumb(pictureId), Icon: FaPalette, color: "#E8C572" },
    canStory && videoId && {
      id: "story", label: "కథ", sub: `LIVE · ${formatSeconds(perf.story_end_sec - perf.story_start_sec)}`, Icon: FaPlay, color: "#f87171",
    },
    canAnalysis && videoId && {
      id: "analysis", label: "విశ్లేషణ", sub: `LIVE · ${formatSeconds(perf.discussion_end_sec - perf.discussion_start_sec)}`, Icon: FaComments, color: "#7dd3fc",
    },
    perf.podcast_audio_url && { id: "podcast", label: "పాడ్‌కాస్ట్", sub: "Podcast", Icon: FaHeadphones, color: "#c4b5fd" },
    ...reels.map((r) => {
      const id = youtubeId(r.youtube_url);
      return {
        id: `reel-${r.reel_id}`, reel: r, label: r.kind === "analysis" ? "విశ్లేషణ రీల్" : "కథ రీల్", sub: "Reel",
        image: id ? thumb(id) : null, Icon: FaFilm, color: "#f9a8d4",
      };
    }),
    vasundhara?.vasundhara_review_youtube_id || vasundhara?.vasundhara_podcast_audio_url
      ? {
          id: "vasundhara", label: "శ్రీమతి వసుంధర", sub: "Review",
          image: vasundhara.vasundhara_review_youtube_id ? thumb(vasundhara.vasundhara_review_youtube_id) : null,
          Icon: FaComments, color: "#fcd34d",
        }
      : null,
    { id: "profile", label: "కథకులు", sub: tellerEn || tellerTe, image: teller?.photo_url, Icon: FaUser, color: "#86efac" },
  ].filter(Boolean);

  const toGraph = (o) => `/episodes/${o.episode_id}/stories/${o.performance_id}?view=graph`;
  const asItems = (list, withTeller = true) =>
    list.map((o) => ({
      key: `${o.performance_id}`,
      title: o.stories.title_telugu,
      sub: withTeller ? tellerOf(o) : fmtDate(showDate(o)),
      to: toGraph(o),
    }));
  // Each group also says what it becomes when it is the centre of the graph.
  const tags = genreTags(story?.genre).map((t) => `#${t}`).join(" ");
  const sameItems = asItems(related?.same || []);
  const writerItems = asItems(related?.writer || []);
  const genreItems = asItems(related?.genre || []);
  const episodeItems = siblings
    .filter((p) => p.performance_id !== perf.performance_id && storyMap[p.story_id]?.title_telugu)
    .map((p) => ({
      key: `${p.performance_id}`,
      title: storyMap[p.story_id].title_telugu,
      sub: tellerOf(p),
      to: `/episodes/${episode.episode_id}/stories/${p.performance_id}?view=graph`,
    }));
  const graphGroups = [
    {
      id: "same", label: "ఈ కథ", sub: "Same story, told again", color: "#c4b5fd", items: sameItems,
      focusTitle: title, focusSub: `${sameItems.length} more tellings`,
    },
    {
      id: "storyteller", label: "కథకులు", sub: tellerEn || tellerTe, color: "#7dd3fc", items: asItems(others, false),
      focusTitle: tellerTe, focusSub: `${others.length} more stories`, focusImage: teller?.photo_url,
    },
    {
      id: "writer", label: "రచయిత", sub: writerTe || writerEn, color: "#6ee7b7", items: writerItems,
      focusTitle: writerTe || writerEn, focusSub: `${writerItems.length} more stories`,
    },
    {
      id: "genre", label: "శైలి", sub: tags, color: "#fda4af", items: genreItems,
      focusTitle: "శైలి", focusSub: tags,
    },
    {
      id: "episode", label: "ఎపిసోడ్", sub: `Episode ${episode.episode_id}`, color: "#fcd34d", items: episodeItems,
      focusTitle: `ఎపిసోడ్ ${episode.episode_id}`,
      focusSub: fmtDate(episode.scheduled_start || episode.publish_date),
      focusImage: videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : null,
    },
  ];

  const embed = (id, extra = "") =>
    `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1${extra}`;
  const renderGraphMedia = (node) => {
    switch (node.id) {
      case "picture":
        return <Frame src={embed(pictureId)} title={node.label} />;
      case "story":
        return <Frame src={embed(videoId, `&start=${Math.floor(perf.story_start_sec)}&end=${Math.ceil(perf.story_end_sec)}`)} title={node.label} />;
      case "analysis":
        return (
          <>
            <Frame
              src={embed(videoId, `&start=${Math.floor(perf.discussion_start_sec)}&end=${Math.ceil(perf.discussion_end_sec)}`)}
              title={node.label}
            />
            {dialog.length > 0 && (
              <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-kk-gold/10 p-2">
                {dialog.map((d, i) => (
                  <p key={i} className="text-sm text-kk-cream/80 leading-relaxed px-1 py-0.5">
                    <span className="font-krushna text-kk-gold/80">{d.speaker_te || d.speaker_en}: </span>
                    {d.text}
                  </p>
                ))}
              </div>
            )}
          </>
        );
      case "podcast":
        return <audio src={perf.podcast_audio_url} controls autoPlay className="w-full" />;
      case "vasundhara":
        return (
          <>
            {vasundhara.vasundhara_review_youtube_id && <Frame src={embed(vasundhara.vasundhara_review_youtube_id)} title={node.label} />}
            {vasundhara.vasundhara_podcast_audio_url && (
              <audio src={vasundhara.vasundhara_podcast_audio_url} controls className="w-full mt-3" />
            )}
            {vasundhara.vasundhara_transcript && (
              <p className="mt-3 text-kk-cream/80 text-[15px] leading-relaxed whitespace-pre-line">{vasundhara.vasundhara_transcript}</p>
            )}
          </>
        );
      case "profile":
        return (
          <div className="flex flex-col sm:flex-row gap-4">
            {teller?.photo_url && (
              <img src={teller.photo_url} alt="" referrerPolicy="no-referrer" className="w-32 h-32 rounded-xl object-cover flex-shrink-0" />
            )}
            <p className="text-kk-cream/80 text-[15px] leading-relaxed whitespace-pre-line">
              {teller?.introduction || "Profile coming soon."}
            </p>
          </div>
        );
      default: {
        const r = node.reel;
        const id = r && youtubeId(r.youtube_url);
        return (
          <div className="flex flex-col items-center gap-3">
            {id && (
              <div className="w-[260px]">
                <Frame src={embed(id)} ratio="177.78%" title={node.label} />
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-1.5">
              {PLATFORMS.filter((p) => r?.[p.key]).map(({ key, label: name, Icon, cls }) => (
                <a key={key} href={r[key]} target="_blank" rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold ${cls}`}>
                  <Icon /> {name}
                </a>
              ))}
            </div>
          </div>
        );
      }
    }
  };

  return (
    <div className="min-h-screen bg-kk-ink text-white">
      {/* ═══ TOP BAR ═══ */}
      <div className="bg-kk-ink/95 border-b border-kk-gold/10 sticky top-0 z-20 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link
            to={`/episodes/${episode.episode_id}`}
            className="flex items-center gap-2 text-kk-gold/70 hover:text-kk-gold transition-colors text-sm min-w-0"
          >
            <FaArrowLeft className="text-xs flex-shrink-0" />
            <span className="truncate">
              <span className="font-krushna">కథాకళ</span> · Episode {episode.episode_id} ·{" "}
              {fmtDate(episode.scheduled_start || episode.publish_date)}
            </span>
          </Link>
          {siblings.length > 1 && (
            <div className="flex items-center gap-2 text-xs text-kk-gold/50 flex-shrink-0">
              <button
                disabled={!prev}
                onClick={() => prev && navigate(`/episodes/${episode.episode_id}/stories/${prev.performance_id}`)}
                className="p-1 hover:text-kk-gold disabled:opacity-20"
                aria-label="Previous story"
              >
                <FaChevronLeft />
              </button>
              <span className="text-kk-cream/40">
                {idx + 1} / {siblings.length}
              </span>
              <button
                disabled={!next}
                onClick={() => next && navigate(`/episodes/${episode.episode_id}/stories/${next.performance_id}`)}
                className="p-1 hover:text-kk-gold disabled:opacity-20"
                aria-label="Next story"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-5">
        {/* ═══ TITLE ═══ */}
        <div className="flex items-start gap-3 mb-4">
          {teller?.photo_url && (
            <img
              src={teller.photo_url}
              alt=""
              referrerPolicy="no-referrer"
              className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover border border-kk-gold/30 flex-shrink-0"
            />
          )}
          <div className="min-w-0">
            <h1 className="font-krushna text-kk-cream text-2xl md:text-3xl leading-tight">{title}</h1>
            {/* Credits: one name per line, larger than body text. */}
            <p className="mt-1.5 leading-snug">
              <span className="text-kk-gold-light/50 text-sm">కథనం: </span>
              <span className="font-krushna text-kk-gold-light text-lg md:text-xl">{tellerTe}</span>
              {tellerEn && <span className="text-kk-gold-light/40 text-sm"> · {tellerEn}</span>}
            </p>
            {writerTe && (
              <p className="leading-snug">
                {folk ? (
                  <span className="font-krushna text-kk-gold-light text-lg md:text-xl">{writerTe}</span>
                ) : (
                  <>
                    <span className="text-kk-gold-light/50 text-sm">రచన: </span>
                    <span className="font-krushna text-kk-gold-light text-lg md:text-xl">{writerTe}</span>
                    {writerEn && <span className="text-kk-gold-light/40 text-sm"> · {writerEn}</span>}
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        {/* ═══ PAGE / కథాజాలం · STORY GRAPH ═══ */}
        <div className="inline-flex rounded-full border border-kk-gold/25 p-0.5 mb-3 text-xs">
          {[
            ["page", "పేజీ", "Page", FaColumns],
            ["graph", "కథాజాలం", "Story Graph", FaProjectDiagram],
          ].map(([id, te, en, Icon]) => (
            <button
              key={id}
              type="button"
              onClick={() => setView(id)}
              className={`flex items-center gap-1.5 px-3.5 py-1 rounded-full transition-colors ${
                view === id ? "bg-kk-gold text-kk-ink" : "text-kk-cream/70 hover:text-kk-cream"
              }`}
            >
              <Icon className="text-[11px]" />
              <span className="font-krushna text-sm">{te}</span>
              <span className="opacity-70">{en}</span>
            </button>
          ))}
        </div>

        {/* ═══ PICTURE STORY / LIVE STORY / LIVE ANALYSIS ═══ */}
        {view === "page" && (pictureId || canStory || canAnalysis) && (
          <div className="flex flex-wrap gap-2 mb-3">
            {pictureId && (
              <ModeButton
                active={effectiveMode === "picture"}
                onClick={showPicture}
                Icon={FaPalette}
                label="చిత్ర కథ"
                sub="Picture story"
              />
            )}
            {canStory && (
              <ModeButton
                active={effectiveMode === "story"}
                onClick={() => play("story")}
                Icon={FaPlay}
                label="కథ వినండి"
                sub={`${pictureId ? "LIVE story" : "Story"} · ${formatSeconds(perf.story_end_sec - perf.story_start_sec)}`}
              />
            )}
            {canAnalysis && (
              <ModeButton
                active={effectiveMode === "analysis"}
                onClick={() => {
                  play("analysis");
                  if (dialog.length) setTab("dialog");
                }}
                Icon={FaComments}
                label="విశ్లేషణ వినండి"
                sub={`${pictureId ? "LIVE analysis" : "Analysis"} · ${formatSeconds(perf.discussion_end_sec - perf.discussion_start_sec)}`}
              />
            )}
          </div>
        )}
      </div>

      {view === "page" && pictureId && (
        <section className={`bg-black ${effectiveMode === "picture" ? "" : "hidden"}`}>
          <div className="max-w-5xl mx-auto">
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <iframe
                data-pausable
                ref={pictureRef}
                src={`https://www.youtube-nocookie.com/embed/${pictureId}?rel=0&modestbranding=1&playsinline=1&enablejsapi=1`}
                title={`చిత్ర కథ — ${title}`}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {view === "page" && videoId && (canStory || canAnalysis) && (
        <KathakalaPlayer
          className={effectiveMode === "picture" ? "hidden" : ""}
          ref={playerRef}
          videoId={videoId}
          startSec={initialSegment[0]}
          endSec={initialSegment[1]}
          autoplay={false}
          onTimeUpdate={setNow}
          nowPlayingTitle={effectiveMode === "analysis" ? `విశ్లేషణ — ${title}` : title}
          nowPlayingMeta={`${formatSeconds(segment[0])} – ${formatSeconds(segment[1])}`}
          youtubeUrl={`https://www.youtube.com/watch?v=${videoId}&t=${Math.floor(segment[0] || 0)}s`}
        />
      )}

      <div className="max-w-5xl mx-auto px-4 py-5 space-y-6">
        {view === "graph" && (
          <StoryGraph
            center={{
              title,
              subtitle: tellerTe,
              photo: teller?.photo_url,
              onClick: () => setView("page"),
            }}
            media={graphMedia}
            groups={graphGroups}
            renderMedia={renderGraphMedia}
          />
        )}

        {view === "page" && (
          <>
        {/* ═══ PODCAST ═══ */}
        {perf.podcast_audio_url && (
          <section className="rounded-xl border border-kk-gold/15 bg-white/[0.03] p-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-9 h-9 rounded-full bg-kk-gold/15 text-kk-gold flex items-center justify-center flex-shrink-0">
                <FaHeadphones />
              </span>
              <div className="min-w-0">
                <p className="font-krushna text-kk-gold text-lg leading-tight">పాడ్‌కాస్ట్ వినండి</p>
                <p className="text-kk-cream/50 text-xs">
                  Podcast · introduction, story and analysis{podcastLength ? ` · ${formatSeconds(podcastLength)}` : ""}
                </p>
              </div>
            </div>
            <audio
              src={perf.podcast_audio_url}
              controls
              preload="metadata"
              className="w-full"
              onPlay={(e) => pauseAll(e.currentTarget)}
              onLoadedMetadata={(e) => Number.isFinite(e.currentTarget.duration) && setPodcastLength(e.currentTarget.duration)}
            />
          </section>
        )}

        {/* ═══ REELS + RELATED STORIES ═══ */}
        <section className={`grid gap-5 ${reels.length ? "md:grid-cols-[auto_minmax(0,1fr)]" : ""}`}>
          {reels.length > 0 && (
            <div className="min-w-0 md:max-w-[440px]">
              <p className="text-kk-gold/80 text-sm mb-2">
                <FaFilm className="inline mr-1.5 text-xs" />
                <span className="font-krushna text-base">రీల్స్</span> · Reels
              </p>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {reels.map((r) => (
                  <ReelCard key={r.reel_id} reel={r} onPlay={() => pauseAll()} />
                ))}
              </div>
            </div>
          )}
          <RelatedStories
            loading={!related}
            groups={[
              { id: "same", label: "ఈ కథ", sub: "Same story, told again", items: related?.same || [] },
              { id: "storyteller", label: "కథకులు", sub: tellerEn || tellerTe, items: others },
              {
                id: "writer",
                label: "రచయిత",
                sub: writerTe || writerEn || "Writer",
                items: related?.writer || [],
              },
              { id: "genre", label: "శైలి", sub: genreTags(story?.genre).map((t) => `#${t}`).join(" "), items: related?.genre || [] },
            ]}
          />
        </section>

        {/* ═══ TABS ═══ */}
        <div>
          <div className="flex gap-1 border-b border-kk-gold/15 mb-4 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-3 md:px-4 py-2 -mb-px border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === t.id
                    ? "border-kk-gold text-kk-gold"
                    : "border-transparent text-kk-cream/50 hover:text-kk-cream/80"
                }`}
              >
                <t.Icon className="text-xs" />
                <span className="font-krushna text-base">{t.label}</span>
                <span className="text-[11px] opacity-60 hidden sm:inline">{t.sub}</span>
              </button>
            ))}
          </div>

          {activeTab === "dialog" && (
            <div>
              {effectiveMode !== "analysis" && (
                <p className="text-kk-cream/50 text-xs mb-2">
                  Press “విశ్లేషణ వినండి” or any line to hear the analysis; the line being spoken lights up.
                </p>
              )}
              <div
                ref={dialogBoxRef}
                className="relative max-h-[28rem] overflow-y-auto rounded-xl border border-kk-gold/10 bg-white/[0.02] p-2 md:p-3"
              >
                {dialog.map((d, i) => {
                  const key = d.speaker_en || d.speaker_te || "?";
                  const newSpeaker = i === 0 || (dialog[i - 1].speaker_en || dialog[i - 1].speaker_te) !== key;
                  const active = i === activeLine;
                  return (
                    <button
                      key={i}
                      ref={(el) => (lineRefs.current[i] = el)}
                      type="button"
                      onClick={() => play("analysis", d.start_sec)}
                      className={`block w-full text-left rounded-lg px-3 py-1.5 transition-colors ${
                        newSpeaker && i > 0 ? "mt-2" : ""
                      } ${active ? "bg-kk-gold/15 ring-1 ring-kk-gold/40" : "hover:bg-white/5"}`}
                    >
                      {newSpeaker && (
                        <span className={`block font-krushna text-sm ${speakerColor[key]}`}>
                          {d.speaker_te || d.speaker_en}
                        </span>
                      )}
                      <span className="flex gap-3">
                        <span className="text-[11px] text-kk-cream/30 font-mono tabular-nums pt-0.5 flex-shrink-0">
                          {formatSeconds(d.start_sec)}
                        </span>
                        <span className={`text-[15px] leading-relaxed ${active ? "text-kk-cream" : "text-kk-cream/75"}`}>
                          {d.text}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === "storyteller" && (
            <div className="space-y-5">
              <div className="flex flex-col sm:flex-row gap-5">
                {teller?.photo_url && (
                  <img
                    src={teller.photo_url}
                    alt={tellerEn || tellerTe || ""}
                    referrerPolicy="no-referrer"
                    className="w-36 h-36 rounded-xl object-cover border border-kk-gold/20 flex-shrink-0"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <h2 className="font-krushna text-kk-gold text-2xl leading-tight">{tellerTe}</h2>
                  {tellerEn && <p className="text-kk-cream/50 text-sm mb-3">{tellerEn}</p>}
                  {teller?.introduction ? (
                    <p className="text-kk-cream/80 text-[15px] leading-relaxed whitespace-pre-line">{teller.introduction}</p>
                  ) : (
                    <p className="text-kk-cream/40 text-sm">Profile coming soon.</p>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* ═══ శ్రీమతి వసుంధర విశ్లేషణ ═══ */}
        {(vasundhara?.vasundhara_review_youtube_id || vasundhara?.vasundhara_podcast_audio_url) && (
          <section className="rounded-xl border border-kk-gold/15 bg-white/[0.03] p-4 md:p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
              <p>
                <span className="font-krushna text-kk-gold text-xl">శ్రీమతి వసుంధర విశ్లేషణ</span>
                <span className="text-kk-cream/50 text-xs ml-2">Smt. Vasundhara's review</span>
              </p>
              <span className="flex gap-3 text-xs">
                <a href={VASUNDHARA_PLAYLIST} target="_blank" rel="noopener noreferrer" className="text-kk-gold-light/70 hover:text-kk-gold">
                  <FaYoutube className="inline mr-1" />All her reviews
                </a>
                <a href={VASUNDHARA_FEED} target="_blank" rel="noopener noreferrer" className="text-kk-gold-light/70 hover:text-kk-gold">
                  <FaHeadphones className="inline mr-1" />Podcast feed
                </a>
              </span>
            </div>
            {vasundhara.vasundhara_review_youtube_id && (
              <div className="max-w-3xl">
                <LazyYouTube
                  id={vasundhara.vasundhara_review_youtube_id}
                  title={`శ్రీమతి వసుంధర విశ్లేషణ — ${title}`}
                  ratio="56.25%"
                  onPlay={() => pauseAll()}
                />
              </div>
            )}
            {vasundhara.vasundhara_podcast_audio_url && (
              <audio
                src={vasundhara.vasundhara_podcast_audio_url}
                controls
                preload="metadata"
                className="w-full max-w-3xl mt-3"
                onPlay={(e) => pauseAll(e.currentTarget)}
              />
            )}
            {vasundhara.vasundhara_transcript && (
              <details className="mt-3 max-w-3xl group">
                <summary className="cursor-pointer text-kk-gold/80 hover:text-kk-gold text-sm select-none">
                  <span className="font-krushna text-base">విశ్లేషణ పాఠం</span> · Read what she said
                </summary>
                <p className="mt-2 text-kk-cream/80 text-[15px] leading-relaxed whitespace-pre-line">
                  {vasundhara.vasundhara_transcript}
                </p>
              </details>
            )}
          </section>
        )}

        {/* ═══ OTHER STORIES IN THIS EPISODE ═══ */}
        {siblings.length > 1 && (
          <div>
            <p className="text-kk-gold/70 text-sm mb-2">
              <span className="font-krushna text-base">ఈ ఎపిసోడ్‌లో ఇతర కథలు</span> · Also in this episode
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {siblings
                .filter((p) => p.performance_id !== perf.performance_id)
                .map((p) => (
                  <Link
                    key={p.performance_id}
                    to={`/episodes/${episode.episode_id}/stories/${p.performance_id}`}
                    className="flex items-center gap-3 bg-white/5 border border-kk-gold/10 rounded-xl p-3 hover:border-kk-gold/30 transition-colors"
                  >
                    <FaPlay className="text-kk-gold/60 text-xs flex-shrink-0" />
                    <span className="min-w-0">
                      <span className="block font-krushna text-kk-cream truncate">
                        {storyMap[p.story_id]?.title_telugu || "—"}
                      </span>
                      <span className="block text-kk-cream/50 text-xs truncate">{tellerOf(p)}</span>
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        )}

          </>
        )}

        <FollowCTA shareTitle={`${title} — ${tellerTe || ""}`} />
      </div>
    </div>
  );
}

// A youtube-nocookie player in a fixed-ratio box (story-graph overlays).
const Frame = ({ src, ratio = "56.25%", title }) => (
  <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ paddingBottom: ratio }}>
    <iframe
      src={src}
      title={title}
      className="absolute inset-0 w-full h-full"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      referrerPolicy="strict-origin-when-cross-origin"
      allowFullScreen
    />
  </div>
);

const ModeButton = ({ active, onClick, Icon, label, sub }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2.5 px-4 py-2 rounded-xl border transition-all active:scale-95 ${
      active
        ? "bg-kk-gold text-kk-ink border-kk-gold"
        : "bg-white/5 text-kk-cream/80 border-kk-gold/20 hover:border-kk-gold/50"
    }`}
  >
    <Icon className="text-sm" />
    <span className="text-left leading-tight">
      <span className="block font-krushna text-base">{label}</span>
      <span className={`block text-[11px] ${active ? "text-kk-ink/70" : "text-kk-cream/40"}`}>{sub}</span>
    </span>
  </button>
);

const PLATFORMS = [
  { key: "youtube_url", label: "YouTube Shorts", Icon: FaYoutube, cls: "text-red-400 border-red-500/30 hover:bg-red-600/15" },
  { key: "instagram_url", label: "Instagram", Icon: FaInstagram, cls: "text-pink-300 border-pink-400/30 hover:bg-pink-500/15" },
  { key: "facebook_url", label: "Facebook", Icon: FaFacebookF, cls: "text-sky-300 border-sky-400/30 hover:bg-sky-500/15" },
];

// Related stories in tabs — same story / storyteller / writer / genre (Bhaskar,
// 2026-10-20). Empty groups are hidden; opens on the storyteller tab.
const RelatedStories = ({ groups, loading }) => {
  const [chosen, setChosen] = useState(null);
  const visible = groups.filter((g) => g.items.length > 0);
  if (!visible.length) {
    return loading ? <div className="rounded-xl border border-kk-gold/10 bg-white/[0.02] min-h-[8rem]" /> : null;
  }
  const active =
    visible.find((g) => g.id === chosen) || visible.find((g) => g.id === "storyteller") || visible[0];
  return (
    <div className="min-w-0 rounded-xl border border-kk-gold/15 bg-white/[0.03] p-3 md:p-4">
      <p className="text-kk-gold/80 text-sm mb-2">
        <span className="font-krushna text-base">సంబంధిత కథలు</span> · Related stories
      </p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {visible.map((g) => (
          <button
            key={g.id}
            type="button"
            onClick={() => setChosen(g.id)}
            className={`px-3 py-1 rounded-full border text-xs transition-colors ${
              active.id === g.id
                ? "bg-kk-gold text-kk-ink border-kk-gold"
                : "border-kk-gold/25 text-kk-cream/70 hover:border-kk-gold/50"
            }`}
          >
            <span className="font-krushna text-sm">{g.label}</span> ({g.items.length})
          </button>
        ))}
      </div>
      {active.sub && <p className="text-kk-cream/45 text-xs mb-2 truncate">{active.sub}</p>}
      <div className="max-h-[22rem] overflow-y-auto pr-1 space-y-1">
        {active.items.map((o) => (
          <Link
            key={o.performance_id}
            to={`/episodes/${o.episode_id}/stories/${o.performance_id}`}
            className="flex items-center gap-3 rounded-lg px-2.5 py-2 hover:bg-white/5 transition-colors"
          >
            <FaPlay className="text-kk-gold/60 text-[10px] flex-shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block font-krushna text-kk-cream/90 truncate">{o.stories.title_telugu}</span>
              <span className="block text-kk-cream/45 text-xs truncate">
                {active.id !== "storyteller" && o.storyteller_name_telugu && (
                  <span className="font-krushna text-[13px]">{tellerOf(o)} · </span>
                )}
                {fmtDate(showDate(o))}
                {o.shared?.length > 0 && <span className="text-kk-gold/50"> · {o.shared.map((t) => `#${t}`).join(" ")}</span>}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
};

// A YouTube video that shows its thumbnail first and loads the
// youtube-nocookie player (autoplaying) on tap; `ratio` is the height as a
// share of the width ("177.78%" for a 9:16 Short, "56.25%" for 16:9).
const LazyYouTube = ({ id, title, ratio, onPlay }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="relative w-full rounded-xl overflow-hidden bg-black border border-kk-gold/15" style={{ paddingBottom: ratio }}>
      {playing ? (
        <iframe
          data-pausable
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`}
          title={title}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            onPlay();
            setPlaying(true);
          }}
          className="absolute inset-0 w-full h-full group"
          aria-label={`Play ${title}`}
        >
          <img src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
            <span className="w-12 h-12 rounded-full bg-red-600/90 flex items-center justify-center">
              <FaPlay className="text-white text-sm ml-0.5" />
            </span>
          </span>
        </button>
      )}
    </div>
  );
};

// One reel: plays inline as a vertical YouTube Short, with its Instagram /
// Facebook / YouTube links below.
const ReelCard = ({ reel, onPlay }) => {
  const id = youtubeId(reel.youtube_url);
  const label = reel.kind === "analysis" ? "విశ్లేషణ రీల్" : "కథ రీల్";
  return (
    <div className="w-[170px] sm:w-[200px] flex-shrink-0">
      {id && <LazyYouTube id={id} title={label} ratio="177.78%" onPlay={onPlay} />}
      <p className="font-krushna text-kk-cream/80 text-sm mt-1.5">
        {label}
        {reel.published_at && <span className="font-sans text-kk-cream/30 text-[11px] ml-1.5">{fmtDate(reel.published_at)}</span>}
      </p>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {PLATFORMS.filter((p) => reel[p.key]).map(({ key, label: name, Icon, cls }) => (
          <a
            key={key}
            href={reel[key]}
            target="_blank"
            rel="noopener noreferrer"
            title={name}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold transition-colors ${cls}`}
          >
            <Icon /> {name.replace(" Shorts", "")}
          </a>
        ))}
      </div>
    </div>
  );
};
